<template>
  <!-- =========================================================
    ✅ 分頁六：文章管理（ArticlesPanel）
    - 目的：把 AdminPanel.vue 裡所有文章相關的 code 都搬到這裡
    - 維護：之後要加 Tiptap 功能、圖片、SEO 欄位都在這支做
  ========================================================= -->

  <div class="tab-panel articles-panel">
    <!-- =========================
      ✅ 新增文章（草稿）
    ========================== -->
    <div class="articles-card" style="margin-top: 10px">
      <div style="font-weight: 700; color: #333; font-size: 14px">新增文章</div>

      <div class="row create-row" style="margin-top: 10px">
        <label class="field search-field">
          標題：
          <input
            v-model="articleCreateTitle"
            placeholder="例如：命盤像說明書，不是判決書"
            @input="
              /* ✅ 有標題就自動帶 slug，但不覆蓋你手動改過的 */
              if (!articleCreateSlug)
                articleCreateSlug = makeSlugFromTitle(articleCreateTitle);
            "
          />
        </label>

        <label class="field" style="min-width: 220px">
          slug：
          <input
            v-model="articleCreateSlug"
            placeholder="例如：bazi-as-manual"
          />
        </label>

        <label class="field search-field">
          description：
          <input
            v-model="articleCreateDescription"
            placeholder="摘要（會用在 SEO / 列表顯示）"
          />
        </label>

        <label class="field search-field">
          tags（逗號分隔）：
          <input v-model="articleCreateTagsText" placeholder="隨筆,觀念,紫微" />
        </label>

        <div class="articles-actions">
          <button @click="createArticle()" :disabled="articleCreating">
            {{ articleCreating ? "建立中..." : "建立草稿" }}
          </button>
        </div>
      </div>

      <p v-if="articleCreateError" class="error">{{ articleCreateError }}</p>
      <p
        v-if="articleCreateMsg"
        style="margin-top: 10px; color: #1f6b3a; font-size: 12px"
      >
        {{ articleCreateMsg }}
      </p>
    </div>

    <!-- =========================
      ✅ 篩選列
    ========================== -->
    <div class="row" style="gap: 10px; align-items: flex-end; flex-wrap: wrap">
      <label class="field" style="min-width: 160px">
        狀態：
        <select v-model="articlesStatus" @change="fetchArticlesList()">
          <option value="">全部</option>
          <option value="published">已發布</option>
          <option value="draft">草稿</option>
        </select>
      </label>

      <label class="field" style="min-width: 180px">
        標籤：
        <select v-model="articlesTag" @change="fetchArticlesList()">
          <option value="">全部</option>
          <option v-for="t in articlesTagOptions" :key="t" :value="t">
            {{ t }}
          </option>
        </select>
      </label>

      <label class="field search-field" style="min-width: 240px">
        搜尋：
        <input
          v-model="articlesQ"
          placeholder="搜 slug / title / description"
          @keyup.enter="fetchArticlesList()"
        />
      </label>

      <div class="articles-actions">
        <button @click="fetchArticlesList()">重新整理</button>
      </div>
    </div>

    <!-- =========================
      ✅ 狀態提示
    ========================== -->
    <p v-if="articlesLoading"><em>載入中...</em></p>
    <p v-if="articlesError" class="error">{{ articlesError }}</p>

    <!-- =========================
      ✅ 列表
    ========================== -->
    <div v-if="!articlesLoading" class="articles-card" style="margin-top: 10px">
      <p style="opacity: 0.8">共 {{ articlesTotal }} 篇</p>

      <table class="table" style="width: 100%; margin-top: 8px">
        <thead>
          <tr>
            <th style="width: 120px">日期</th>
            <th>標題</th>
            <th style="width: 90px">狀態</th>
            <th style="width: 220px">標籤</th>
            <th style="width: 220px">slug</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="it in articlesItems"
            :key="it.slug"
            @click="openArticle(it.slug)"
            style="cursor: pointer"
          >
            <td>{{ it.date }}</td>
            <td>{{ it.title }}</td>
            <td>
              <span
                class="badge"
                :class="{
                  ok: it.status === 'published',
                  warn: it.status !== 'published',
                }"
              >
                {{ it.status }}
              </span>
            </td>
            <td>
              <span
                v-for="t in it.tags || []"
                :key="t"
                class="tag"
                style="margin-right: 6px"
              >
                {{ t }}
              </span>
            </td>
            <td>{{ it.slug }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- =========================
      ✅ 編輯卡（Tiptap）
    ========================== -->
    <div
      v-if="articleEditingSlug"
      class="articles-card"
      style="margin-top: 14px"
    >
      <div
        style="
          display: flex;
          justify-content: space-between;
          gap: 12px;
          align-items: flex-start;
        "
      >
        <div>
          <div style="font-weight: 700; color: #333; font-size: 14px">
            編輯文章：{{ articleEditingSlug }}
          </div>
          <div style="color: #666; font-size: 12px; margin-top: 2px">
            {{ articleEditingMeta?.title }}（{{ articleEditingMeta?.status }}）
          </div>
        </div>

        <div class="articles-actions">
          <!-- ✅ 狀態切換 -->
          <button
            class="ghost-btn"
            @click="publishArticle()"
            :disabled="articleSaving || !articleEditingSlug"
            v-if="articleEditingMeta?.status !== 'published'"
          >
            一鍵發布
          </button>

          <button
            class="ghost-btn"
            @click="unpublishArticle()"
            :disabled="articleSaving || !articleEditingSlug"
            v-else
          >
            轉回草稿
          </button>

          <button
            class="ghost-btn"
            @click="deleteArticle()"
            :disabled="articleSaving || !articleEditingSlug"
          >
            刪除文章
          </button>

          <!-- ✅ 儲存 -->
          <button @click="saveArticleHtml()" :disabled="articleSaving">
            {{ articleSaving ? "儲存中..." : "儲存" }}
          </button>

          <!-- ✅ 關閉 -->
          <button
            @click="
              destroyArticleEditor();
              articleEditingSlug = '';
              articleEditingMeta = null;
              articleEditingJson = null;
              articleEditingHtml = '';
              articleEditingError = '';
              articleSaveMsg = '';
            "
            :disabled="articleSaving"
          >
            關閉
          </button>
        </div>
      </div>

      <p v-if="articleEditingLoading"><em>載入中...</em></p>
      <p v-if="articleEditingError" class="error">{{ articleEditingError }}</p>
      <p
        v-if="articleSaveMsg"
        style="margin-top: 10px; color: #1f6b3a; font-size: 12px"
      >
        {{ articleSaveMsg }}
      </p>

      <!-- ✅ Tiptap -->
      <div v-if="!articleEditingLoading" style="margin-top: 12px">
        <div class="field" style="min-width: 100%">
          文章內容（Tiptap）：

          <!-- ✅ 工具列 -->
          <div class="tiptap-toolbar" v-if="articleEditor">
            <button
              class="tb-btn"
              :class="{ active: articleEditor.isActive('bold') }"
              @click="articleEditor.chain().focus().toggleBold().run()"
              :disabled="
                !articleEditor.can().chain().focus().toggleBold().run()
              "
            >
              B
            </button>

            <button
              class="tb-btn"
              :class="{ active: articleEditor.isActive('italic') }"
              @click="articleEditor.chain().focus().toggleItalic().run()"
              :disabled="
                !articleEditor.can().chain().focus().toggleItalic().run()
              "
            >
              I
            </button>

            <button
              class="tb-btn"
              :class="{ active: articleEditor.isActive('underline') }"
              @click="articleEditor.chain().focus().toggleUnderline().run()"
              :disabled="
                !articleEditor.can().chain().focus().toggleUnderline().run()
              "
            >
              U
            </button>

            <button
              class="tb-btn"
              :class="{ active: articleEditor.isActive('strike') }"
              @click="articleEditor.chain().focus().toggleStrike().run()"
              :disabled="
                !articleEditor.can().chain().focus().toggleStrike().run()
              "
            >
              S
            </button>

            <span class="tb-sep"></span>

            <button
              class="tb-btn"
              :class="{
                active: articleEditor.isActive('heading', { level: 2 }),
              }"
              @click="
                articleEditor.chain().focus().toggleHeading({ level: 2 }).run()
              "
            >
              H2
            </button>

            <button
              class="tb-btn"
              :class="{
                active: articleEditor.isActive('heading', { level: 3 }),
              }"
              @click="
                articleEditor.chain().focus().toggleHeading({ level: 3 }).run()
              "
            >
              H3
            </button>

            <span class="tb-sep"></span>

            <!-- ✅ 顏色盤（選了就套） -->
            <input
              type="color"
              v-model="articleTextColor"
              @input="setTextColor()"
              title="選字色"
              style="
                height: 32px;
                width: 40px;
                padding: 0;
                border: 1px solid #ddd;
                border-radius: 8px;
                background: #fff;
              "
            />

            <!-- ✅ 字型下拉（選了就套） -->
            <select
              v-model="articleFontFamily"
              @change="setFontFamily()"
              title="選字型"
              style="
                height: 32px;
                border: 1px solid #ddd;
                border-radius: 8px;
                padding: 0 8px;
                background: #fff;
              "
            >
              <option value="">預設字體</option>
              <option
                v-for="f in articleFontOptions"
                :key="f.value"
                :value="f.value"
              >
                {{ f.label }}
              </option>
            </select>

            <!-- ✅ 字體大小（選了就套） -->
            <select
              v-model="articleFontSize"
              @change="setFontSize()"
              title="字體大小"
              style="
                height: 32px;
                border: 1px solid #ddd;
                border-radius: 8px;
                padding: 0 8px;
                background: #fff;
              "
            >
              <option value="">預設字級</option>
              <option
                v-for="s in articleFontSizeOptions"
                :key="s.value"
                :value="s.value"
              >
                {{ s.label }}
              </option>
            </select>

            <!-- ✅（可選）字級 - / + -->
            <button class="tb-btn" @click="decreaseFontSize()" title="字小">
              A-
            </button>
            <button class="tb-btn" @click="increaseFontSize()" title="字大">
              A+
            </button>

            <button
              class="tb-btn"
              @click="articleEditor.chain().focus().setHorizontalRule().run()"
            >
              ─
            </button>

            <button
              class="tb-btn"
              type="button"
              @mousedown.prevent
              @click="insertImageByUrl()"
            >
              🖼️
            </button>

            <span class="tb-sep"></span>

            <button class="tb-btn" @click="setArticleLink()">🔗</button>

            <button
              class="tb-btn"
              @click="articleEditor.chain().focus().unsetLink().run()"
              :disabled="!articleEditor.isActive('link')"
            >
              取消連結
            </button>

            <span class="tb-sep"></span>

            <!-- ✅ 匯入 HTML：彈窗讓使用者貼 HTML，解析為 Tiptap 內容 -->
            <button
              class="tb-btn"
              type="button"
              @click="openHtmlImport()"
              title="匯入 HTML（從外部貼入）"
            >
              ⬇ HTML
            </button>

            <!--<span class="tb-sep"></span>-->
            <span style="flex-basis: 100%; height: 1px"></span>

            <button
              class="tb-btn"
              @click="articleEditor.chain().focus().undo().run()"
              :disabled="!articleEditor.can().chain().focus().undo().run()"
            >
              ↶
            </button>

            <button
              class="tb-btn"
              @click="articleEditor.chain().focus().redo().run()"
              :disabled="!articleEditor.can().chain().focus().redo().run()"
            >
              ↷
            </button>

            <span class="tb-sep"></span>

            <button
              class="tb-btn"
              @click="
                articleEditor.chain().focus().unsetAllMarks().clearNodes().run()
              "
            >
              清格式
            </button>
          </div>

          <div class="tiptap-shell">
            <EditorContent :editor="articleEditor" />
          </div>

          <!-- ✅ 匯入 HTML 彈窗 -->
          <div v-if="htmlImportOpen" class="html-import-mask" @click.self="closeHtmlImport()">
            <div class="html-import-modal">
              <div class="html-import-head">
                <strong>匯入 HTML</strong>
                <button class="tb-btn" @click="closeHtmlImport()">✕</button>
              </div>
              <p class="html-import-hint">
                貼上 HTML 內容，按「匯入」會解析成編輯器內容。
                <br />
                ⚠️ 會<strong>取代目前所有編輯區內容</strong>。
              </p>
              <textarea
                v-model="htmlImportText"
                class="html-import-textarea"
                rows="14"
                placeholder="<p>貼上 HTML 內容…</p>"
              ></textarea>
              <label class="html-import-mode">
                <input type="checkbox" v-model="htmlImportAppend" />
                改為「附加到現有內容後面」（不取代）
              </label>
              <div class="html-import-actions">
                <button class="tb-btn" @click="closeHtmlImport()">取消</button>
                <button
                  class="tb-btn primary"
                  @click="confirmHtmlImport()"
                  :disabled="!htmlImportText.trim()"
                >
                  匯入
                </button>
              </div>
            </div>
          </div>

          <div style="margin-top: 8px; font-size: 12px; color: #666">
            已同步：JSON（{{ articleEditingJson ? "有" : "無" }}） / HTML（{{
              articleEditingHtml ? "有" : "無"
            }}）
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* =========================================================
  ✅ ArticlesPanel / 引入 Tiptap
========================================================= */
import { Editor, EditorContent } from "@tiptap/vue-3";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import { Mark, mergeAttributes, Extension } from "@tiptap/core";
import Color from "@tiptap/extension-color";
import FontFamily from "@tiptap/extension-font-family";
import Image from "@tiptap/extension-image";

/* =========================================================
  ✅ Step 1：補齊 textStyle 必要 command（讓 unsetColor 不會炸）
========================================================= */
const ExtTextStyle = Mark.create({
  name: "textStyle",
  parseHTML() {
    return [{ tag: "span[style]" }];
  },
  renderHTML({ HTMLAttributes }) {
    return ["span", mergeAttributes(HTMLAttributes), 0];
  },
  addCommands() {
    return {
      removeEmptyTextStyle: () => () => {
        /* ✅ 最小可用：先不做清理，只要回 true 讓 chain 不炸 */
        return true;
      },
    };
  },
});

/* 
==========================================================
✅ FontSize Extension（套用字體大小）
目的：
- 讓你在 toolbar 選 12px/14px/16px... 時
- 對「選取中的文字」套上 style="font-size: XXpx"
依賴：
- 你的 ExtTextStyle（textStyle mark）當作承載 style 的容器
==========================================================
*/
const FontSize = Extension.create({
  name: "fontSize",

  /* 
  ----------------------------------------------------------
  ✅ options：指定要把 fontSize 這個 attribute 掛在哪些 types 上
  - 這裡掛在 textStyle 上（最常見、最穩）
  ----------------------------------------------------------
  */
  addOptions() {
    return {
      types: ["textStyle"],
    };
  },

  /* 
  ----------------------------------------------------------
  ✅ addGlobalAttributes：
  - parseHTML：從 HTML span style 讀回 font-size
  - renderHTML：輸出到 HTML style="font-size: ..."
  ----------------------------------------------------------
  */
  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          fontSize: {
            default: null,

            parseHTML: (element) => {
              const size = element.style?.fontSize || "";
              return size ? String(size).trim() : null;
            },

            renderHTML: (attributes) => {
              if (!attributes.fontSize) return {};
              return {
                style: `font-size: ${attributes.fontSize}`,
              };
            },
          },
        },
      },
    ];
  },

  /* 
  ----------------------------------------------------------
  ✅ commands：
  - setFontSize("16px")：套用字體大小
  - unsetFontSize()：移除字體大小（回到預設）
  ----------------------------------------------------------
  */
  addCommands() {
    return {
      setFontSize:
        (fontSize) =>
        ({ chain }) => {
          if (!fontSize) return true;

          return chain().setMark("textStyle", { fontSize }).run();
        },

      unsetFontSize:
        () =>
        ({ chain }) => {
          return (
            chain()
              .setMark("textStyle", { fontSize: null })
              /* ✅ 你 ExtTextStyle 的 removeEmptyTextStyle 會回 true，避免 chain 炸 */
              .removeEmptyTextStyle()
              .run()
          );
        },
    };
  },
});

export default {
  name: "ArticlesPanel",

  /* =========================================================
    ✅ 由 AdminPanel 傳進來，避免這支元件依賴外部全域 cfg
  ========================================================= */
  props: {
    apiBaseUrl: { type: String, required: true },
    adminToken: { type: String, required: true },
  },

  components: {
    EditorContent,
  },

  data() {
    return {
      // ===== 文章管理（Articles）=====
      articlesItems: [],
      articlesTotal: 0,

      // ✅ 篩選條件
      articlesQ: "",
      articlesTag: "",
      articlesStatus: "",

      // ✅ UI 狀態
      articlesLoading: false,
      articlesError: "",

      // ===== 文章管理：目前編輯中 =====
      articleEditingSlug: "",
      articleEditingMeta: null,
      articleEditingJson: null,
      articleEditingHtml: "",
      articleEditingLoading: false,
      articleEditingError: "",

      // ✅ 儲存狀態（避免連點、給 UI 回饋）
      articleSaving: false,
      articleSaveMsg: "",

      // ===== 文章管理：新增文章表單 =====
      articleCreateTitle: "",
      articleCreateSlug: "",
      articleCreateDescription: "",
      articleCreateTagsText: "",
      articleCreating: false,
      articleCreateError: "",
      articleCreateMsg: "",

      // ✅ Tiptap Editor instance
      articleEditor: null,

      // ✅ 工具列狀態（顏色盤 / 字型下拉）
      articleTextColor: "#111111",
      articleFontFamily: "",
      articleFontOptions: [
        { label: "Noto Sans TC（推薦）", value: "Noto Sans TC, sans-serif" },
        { label: "微軟正黑體", value: "Microsoft JhengHei, sans-serif" },
        { label: "蘋方 / PingFang", value: "PingFang TC, sans-serif" },
        { label: "思源宋體（偏書感）", value: "Noto Serif TC, serif" },
      ],

      /* 
      ----------------------------------------------------------
      ✅ 工具列狀態（字體大小）
      目的：
      - articleFontSize：toolbar 下拉選到的字級
      - articleFontSizeOptions：提供選項
      ----------------------------------------------------------
      */
      articleFontSize: "",
      articleFontSizeOptions: [
        { label: "12", value: "12px" },
        { label: "13", value: "13px" },
        { label: "14", value: "14px" },
        { label: "16", value: "16px" },
        { label: "18", value: "18px" },
        { label: "20", value: "20px" },
        { label: "24", value: "24px" },
      ],

      // ✅ 匯入 HTML 彈窗狀態
      htmlImportOpen: false,
      htmlImportText: "",
      htmlImportAppend: false,
    };
  },

  computed: {
    /* =========================
      ✅ tags 下拉選項：從 items 自動整理
    ========================== */
    articlesTagOptions() {
      const set = new Set();
      (this.articlesItems || []).forEach((it) => {
        (it.tags || []).forEach((t) => set.add(t));
      });
      return Array.from(set).sort();
    },
  },

  mounted() {
    /* =========================
      ✅ 元件出現就抓一次列表（跟你原本點 tab 抓的效果一致）
    ========================== */
    this.fetchArticlesList();
  },

  beforeUnmount() {
    /* =========================
      ✅ 離開頁面銷毀 editor，避免記憶體洩漏
    ========================== */
    this.destroyArticleEditor();
  },

  methods: {
    /* ==========================================================
      ✅ 文章管理：抓文章列表（支援 q/tag/status）
    ========================================================== */
    async fetchArticlesList() {
      this.articlesError = "";
      this.articlesLoading = true;

      try {
        const qs = new URLSearchParams();
        if (this.articlesQ) qs.set("q", this.articlesQ);
        if (this.articlesTag) qs.set("tag", this.articlesTag);
        if (this.articlesStatus) qs.set("status", this.articlesStatus);

        const url = `${this.apiBaseUrl}/api/admin/articles?${qs.toString()}`;

        const res = await fetch(url, {
          method: "GET",
          headers: { "x-admin-token": this.adminToken },
        });

        const ct = res.headers.get("content-type") || "";
        const data = ct.includes("application/json")
          ? await res.json().catch(() => null)
          : await res.text().catch(() => null);

        if (!res.ok) {
          const msg = (data && data.message) || `HTTP ${res.status}`;
          throw new Error(msg);
        }

        this.articlesItems = (data && data.items) || [];
        this.articlesTotal = (data && data.total) || 0;
      } catch (err) {
        this.articlesError = String(
          err?.message || err || "FETCH_ARTICLES_FAILED",
        );
      } finally {
        this.articlesLoading = false;
      }
    },

    /* ==========================================================
      ✅ 開啟單篇文章（載入到編輯區）
    ========================================================== */
    async openArticle(slug) {
      this.articleEditingError = "";
      this.articleEditingLoading = true;

      try {
        const url = `${this.apiBaseUrl}/api/admin/articles/${encodeURIComponent(
          slug,
        )}`;

        const res = await fetch(url, {
          method: "GET",
          headers: { "x-admin-token": this.adminToken },
        });

        const ct = res.headers.get("content-type") || "";
        const data = ct.includes("application/json")
          ? await res.json().catch(() => null)
          : await res.text().catch(() => null);

        if (!res.ok) {
          const msg = (data && data.message) || `HTTP ${res.status}`;
          throw new Error(msg);
        }

        this.articleEditingSlug = slug;
        this.articleEditingMeta = data.meta || null;
        this.articleEditingJson = data.content_json || null;
        this.articleEditingHtml = data.content_html || "";

        this.initArticleEditor();
      } catch (err) {
        this.articleEditingError = String(
          err?.message || err || "OPEN_ARTICLE_FAILED",
        );
      } finally {
        this.articleEditingLoading = false;
      }
    },

    /* ==========================================================
      ✅ 儲存（PATCH）：一次送 HTML + JSON
    ========================================================== */
    async saveArticleHtml() {
      if (!this.articleEditingSlug) return;

      this.articleSaveMsg = "";
      this.articleEditingError = "";
      this.articleSaving = true;

      try {
        const url = `${this.apiBaseUrl}/api/admin/articles/${encodeURIComponent(
          this.articleEditingSlug,
        )}`;

        const res = await fetch(url, {
          method: "PATCH",
          headers: {
            "x-admin-token": this.adminToken,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            content_html: this.articleEditingHtml,
            content_json: this.articleEditingJson,
          }),
        });

        const ct = res.headers.get("content-type") || "";
        const data = ct.includes("application/json")
          ? await res.json().catch(() => null)
          : await res.text().catch(() => null);

        if (!res.ok) {
          const msg = (data && data.message) || `HTTP ${res.status}`;
          throw new Error(msg);
        }

        this.articleSaveMsg = "已儲存 ✅";
        await this.openArticle(this.articleEditingSlug);
        await this.fetchArticlesList();
      } catch (err) {
        this.articleEditingError = String(err?.message || err || "SAVE_FAILED");
      } finally {
        this.articleSaving = false;
      }
    },

    /* ==========================================================
      ✅ 發布 / 轉回草稿
    ========================================================== */
    async publishArticle() {
      if (!this.articleEditingSlug) return;
      if (this.articleSaving) return;

      this.articleSaveMsg = "";
      this.articleEditingError = "";
      this.articleSaving = true;

      try {
        const url = `${this.apiBaseUrl}/api/admin/articles/${encodeURIComponent(
          this.articleEditingSlug,
        )}`;

        const res = await fetch(url, {
          method: "PATCH",
          headers: {
            "x-admin-token": this.adminToken,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            status: "published",
            content_html: this.articleEditingHtml,
            content_json: this.articleEditingJson,
          }),
        });

        const ct = res.headers.get("content-type") || "";
        const data = ct.includes("application/json")
          ? await res.json().catch(() => null)
          : await res.text().catch(() => null);

        if (!res.ok) {
          const msg = (data && data.message) || `HTTP ${res.status}`;
          throw new Error(msg);
        }

        this.articleSaveMsg = "已發布 ✅";
        await this.openArticle(this.articleEditingSlug);
        await this.fetchArticlesList();
      } catch (err) {
        this.articleEditingError = String(
          err?.message || err || "PUBLISH_FAILED",
        );
      } finally {
        this.articleSaving = false;
      }
    },

    async unpublishArticle() {
      if (!this.articleEditingSlug) return;
      if (this.articleSaving) return;

      this.articleSaveMsg = "";
      this.articleEditingError = "";
      this.articleSaving = true;

      try {
        const url = `${this.apiBaseUrl}/api/admin/articles/${encodeURIComponent(
          this.articleEditingSlug,
        )}`;

        const res = await fetch(url, {
          method: "PATCH",
          headers: {
            "x-admin-token": this.adminToken,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            status: "draft",
            content_html: this.articleEditingHtml,
            content_json: this.articleEditingJson,
          }),
        });

        const ct = res.headers.get("content-type") || "";
        const data = ct.includes("application/json")
          ? await res.json().catch(() => null)
          : await res.text().catch(() => null);

        if (!res.ok) {
          const msg = (data && data.message) || `HTTP ${res.status}`;
          throw new Error(msg);
        }

        this.articleSaveMsg = "已轉回草稿 ✅";
        await this.openArticle(this.articleEditingSlug);
        await this.fetchArticlesList();
      } catch (err) {
        this.articleEditingError = String(
          err?.message || err || "UNPUBLISH_FAILED",
        );
      } finally {
        this.articleSaving = false;
      }
    },

    /* ==========================================================
      ✅ 刪除文章（DELETE）
    ========================================================== */
    async deleteArticle() {
      if (!this.articleEditingSlug) return;
      if (this.articleSaving) return;

      const slug = this.articleEditingSlug;
      if (!confirm(`確定要刪除這篇文章嗎？（${slug}）\n刪掉就回不來。`)) return;

      this.articleSaveMsg = "";
      this.articleEditingError = "";
      this.articleSaving = true;

      try {
        const url = `${this.apiBaseUrl}/api/admin/articles/${encodeURIComponent(
          slug,
        )}`;

        const res = await fetch(url, {
          method: "DELETE",
          headers: { "x-admin-token": this.adminToken },
        });

        const text = await res.text().catch(() => "");
        let data = null;
        try {
          data = JSON.parse(text);
        } catch {
          data = text;
        }

        if (!res.ok) {
          const msg = (data && data.message) || `HTTP ${res.status}`;
          throw new Error(msg);
        }

        this.destroyArticleEditor();
        this.articleEditingSlug = "";
        this.articleEditingMeta = null;
        this.articleEditingJson = null;
        this.articleEditingHtml = "";
        this.articleEditingError = "";
        this.articleSaveMsg = "";

        await this.fetchArticlesList();
        alert("已刪除 ✅");
      } catch (err) {
        this.articleEditingError = String(
          err?.message || err || "DELETE_FAILED",
        );
      } finally {
        this.articleSaving = false;
      }
    },

    /* ==========================================================
      ✅ 新增文章（POST）
    ========================================================== */
    async createArticle() {
      this.articleCreateError = "";
      this.articleCreateMsg = "";

      const title = String(this.articleCreateTitle || "").trim();
      const slug = String(this.articleCreateSlug || "").trim();
      const description = String(this.articleCreateDescription || "").trim();

      if (!title) {
        this.articleCreateError = "請先填標題";
        return;
      }
      if (!slug) {
        this.articleCreateError = "請先填 slug（或讓系統自動帶入）";
        return;
      }

      const tags = String(this.articleCreateTagsText || "")
        .split(/[，,]/g)
        .map((s) => s.trim())
        .filter(Boolean);

      this.articleCreating = true;

      try {
        const url = `${this.apiBaseUrl}/api/admin/articles`;

        const res = await fetch(url, {
          method: "POST",
          headers: {
            "x-admin-token": this.adminToken,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            slug,
            title,
            description,
            tags,
            status: "draft",
            content_html: "<p></p>",
            content_json: { type: "doc", content: [{ type: "paragraph" }] },
          }),
        });

        const ct = res.headers.get("content-type") || "";
        const data = ct.includes("application/json")
          ? await res.json().catch(() => null)
          : await res.text().catch(() => null);

        if (!res.ok) {
          const msg = (data && data.message) || `HTTP ${res.status}`;
          throw new Error(msg);
        }

        this.articleCreateMsg = `已建立草稿 ✅（${slug}）`;

        this.articleCreateTitle = "";
        this.articleCreateSlug = "";
        this.articleCreateDescription = "";
        this.articleCreateTagsText = "";

        await this.fetchArticlesList();
        await this.openArticle(slug);
      } catch (err) {
        this.articleCreateError = String(
          err?.message || err || "CREATE_FAILED",
        );
      } finally {
        this.articleCreating = false;
      }
    },

    /* ==========================================================
      ✅ Slug 工具
    ========================================================== */
    makeSlugFromTitle(title) {
      const t = String(title || "").trim();
      if (!t) return "";

      const s = t
        .toLowerCase()
        .replace(/['"]/g, "")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "")
        .slice(0, 60);

      if (!s) {
        const d = new Date();
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");
        return `article-${y}${m}${day}`;
      }

      return s;
    },

    /* ==========================================================
      ✅ Tiptap：初始化 / 銷毀
    ========================================================== */
    initArticleEditor() {
      if (this.articleEditor) {
        try {
          this.articleEditor.destroy();
        } catch (e) {
          // ✅ ignore
        }
        this.articleEditor = null;
      }

      const initialJSON = this.articleEditingJson || null;
      const initialHTML = this.articleEditingHtml || "";

      this.articleEditor = new Editor({
        extensions: [
          StarterKit,
          Underline,
          Link.configure({
            openOnClick: false,
            autolink: true,
            linkOnPaste: true,
          }),
          ExtTextStyle,
          FontSize,
          Color.configure({ types: ["textStyle"] }),
          FontFamily.configure({ types: ["textStyle"] }),

          Image.configure({
            inline: false,
            allowBase64: true /* ✅ 之後你若要 base64 也行（但注意 HTML 會變大） */,
          }),
        ],
        content: initialJSON ? initialJSON : initialHTML,
        onUpdate: ({ editor }) => {
          this.articleEditingJson = editor.getJSON();
          this.articleEditingHtml = editor.getHTML();
        },
      });

      this.articleEditingJson = this.articleEditor.getJSON();
      this.articleEditingHtml = this.articleEditor.getHTML();

      /* 
      ==========================================================
      ✅ 同步 toolbar 狀態：游標在哪，字級下拉就顯示哪個字級
      目的：
      - 選取文字或移動游標時，讀取 textStyle.fontSize
      - 回填到 articleFontSize（下拉顯示）
      ==========================================================
      */
      const syncToolbarState = () => {
        if (!this.articleEditor) return;

        /* 
        ----------------------------------------------------------
        ✅ 讀取目前 selection 的 textStyle attribute
        - 有套 font-size 時：會拿到 "16px" 之類
        - 沒套時：回 null → 我們就顯示「預設字級」
        ----------------------------------------------------------
        */
        const attrs = this.articleEditor.getAttributes("textStyle") || {};
        const size = attrs.fontSize ? String(attrs.fontSize).trim() : "";

        /* ✅ 避免一直重設造成不必要 re-render */
        if (this.articleFontSize !== size) this.articleFontSize = size;

        /* （可選）你也可以順便同步字型下拉 */
        const font = attrs.fontFamily ? String(attrs.fontFamily).trim() : "";
        if (this.articleFontFamily !== font) this.articleFontFamily = font;

        /* （可選）同步顏色 */
        const color = attrs.color ? String(attrs.color).trim() : "";
        if (color && this.articleTextColor !== color)
          this.articleTextColor = color;
      };

      /* ✅ selection 移動/選取變動時觸發 */
      this.articleEditor.on("selectionUpdate", syncToolbarState);

      /* ✅ 有些情況 style 變了但 selection 不動，用 transaction 補齊 */
      this.articleEditor.on("transaction", syncToolbarState);

      /* ✅ 初始化先同步一次 */
      syncToolbarState();
    },

    destroyArticleEditor() {
      if (!this.articleEditor) return;
      try {
        this.articleEditor.destroy();
      } catch (e) {
        // ✅ ignore: destroy 失敗不應阻斷 UI
      }
      this.articleEditor = null;
    },

    /* ==========================================================
      ✅ Tiptap：字色 / 字型 / 連結
    ========================================================== */
    setTextColor() {
      if (!this.articleEditor) return;
      const color = String(this.articleTextColor || "").trim();
      if (!color) return;
      this.articleEditor.chain().focus().setColor(color).run();
    },

    unsetTextColor() {
      if (!this.articleEditor) return;
      this.articleEditor.chain().focus().unsetColor().run();
    },

    setFontFamily() {
      if (!this.articleEditor) return;
      const font = String(this.articleFontFamily || "").trim();
      if (!font) {
        this.articleEditor.chain().focus().unsetFontFamily().run();
        return;
      }
      this.articleEditor.chain().focus().setFontFamily(font).run();
    },

    unsetFontFamily() {
      if (!this.articleEditor) return;
      this.articleEditor.chain().focus().unsetFontFamily().run();
    },

    /* ==========================================================
      ✅ Tiptap：字體大小
    ========================================================== */
    setFontSize() {
      if (!this.articleEditor) return;

      const size = String(this.articleFontSize || "").trim();

      /* ✅ 選「預設」就移除 font-size */
      if (!size) {
        this.articleEditor.chain().focus().unsetFontSize().run();
        return;
      }

      /* ✅ 套用字體大小 */
      this.articleEditor.chain().focus().setFontSize(size).run();
    },

    unsetFontSize() {
      if (!this.articleEditor) return;
      this.articleEditor.chain().focus().unsetFontSize().run();
    },

    /* 
    ----------------------------------------------------------
    ✅ （可選）字級加減：用現有選項前後切換
    - 沒選就從 14px 起跳
    ----------------------------------------------------------
    */
    increaseFontSize() {
      const opts = (this.articleFontSizeOptions || []).map((x) => x.value);
      const curr = this.articleFontSize || "14px";
      const idx = Math.max(0, opts.indexOf(curr));
      const next = opts[Math.min(opts.length - 1, idx + 1)];
      this.articleFontSize = next;
      this.setFontSize();
    },

    decreaseFontSize() {
      const opts = (this.articleFontSizeOptions || []).map((x) => x.value);
      const curr = this.articleFontSize || "14px";
      const idx = Math.max(0, opts.indexOf(curr));
      const prev = opts[Math.max(0, idx - 1)];
      this.articleFontSize = prev;
      this.setFontSize();
    },

    setArticleLink() {
      if (!this.articleEditor) return;

      const prev = this.articleEditor.getAttributes("link").href || "";
      const url = window.prompt("輸入連結 URL（https://...）", prev);
      if (url === null) return;

      const u = String(url || "").trim();
      if (!u) {
        this.articleEditor.chain().focus().unsetLink().run();
        return;
      }

      this.articleEditor.chain().focus().setLink({ href: u }).run();
    },

    /* ==========================================================
      ✅ 匯入 HTML：開啟 / 關閉 / 確認
      目的：
      - 直接貼 HTML（從外部 AI / 文章生成器產出）會被瀏覽器當純文字
      - 改用彈窗 textarea 收 HTML，再用 Tiptap 的 setContent 解析
    ========================================================== */
    openHtmlImport() {
      this.htmlImportText = "";
      this.htmlImportAppend = false;
      this.htmlImportOpen = true;
    },

    closeHtmlImport() {
      this.htmlImportOpen = false;
    },

    confirmHtmlImport() {
      if (!this.articleEditor) return;
      const html = String(this.htmlImportText || "").trim();
      if (!html) return;

      try {
        if (this.htmlImportAppend) {
          /* ✅ 附加：插在游標位置 */
          this.articleEditor.chain().focus().insertContent(html).run();
        } else {
          /* ✅ 取代：整篇換掉 */
          this.articleEditor.commands.setContent(html, true);
        }
        /* ✅ 同步 JSON / HTML，避免存檔時資料不同步 */
        this.articleEditingJson = this.articleEditor.getJSON();
        this.articleEditingHtml = this.articleEditor.getHTML();

        this.closeHtmlImport();
      } catch (err) {
        alert("HTML 解析失敗：" + (err?.message || err));
      }
    },

    /* ==========================================================
      ✅ 插圖：用 URL 插入圖片
    ========================================================== */
    insertImageByUrl() {
      if (!this.articleEditor) return;

      const url = window.prompt("輸入圖片網址（https://...）");
      if (url === null) return;

      const src = String(url || "").trim();
      if (!src) return;

      /* ✅ 插入圖片 */
      this.articleEditor.chain().focus().setImage({ src }).run();
    },
  },
};
</script>

<style scoped>
/* ==========================================================
  ✅ ArticlesPanel：沿用你後台白底卡片風格
========================================================== */

/* ✅ 篩選列：卡片工具列 */
.articles-panel .row {
  display: flex;
  gap: 14px;
  flex-wrap: wrap;
  align-items: flex-end;
  margin-top: 10px;

  border: 1px solid #eee;
  border-radius: 12px;
  background: #fff;
  padding: 14px;
}

/* ✅ 欄位 label */
.articles-panel .field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 12px;
  color: #666;
  min-width: 160px;
}

/* ✅ input / select */
.articles-panel input,
.articles-panel select {
  width: 100%;
  height: 36px;
  padding: 0 12px;
  font-size: 13px;
  line-height: 1.4;

  border-radius: 10px;
  border: 1px solid #ddd;
  background: #fafafa;
  color: #333;
  outline: none;
}

.articles-panel input:focus,
.articles-panel select:focus {
  border-color: #bbb;
  background: #fff;
}

/* ✅ 按鈕 */
.articles-panel button {
  height: 36px;
  padding: 0 14px;
  border-radius: 10px;
  border: 1px solid #ddd;
  background: #fff;
  color: #333;
  cursor: pointer;
}

.articles-panel button:hover {
  background: rgba(0, 0, 0, 0.03);
}

/* ✅ 文章列表卡片 */
.articles-card {
  border: 1px solid #eee;
  border-radius: 12px;
  background: #fff;
  padding: 14px;
}

/* ✅ table */
.articles-panel table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 10px;
}

.articles-panel thead th {
  text-align: left;
  font-size: 12px;
  color: #666;
  padding: 10px 8px;
  border-bottom: 1px solid #eee;
}

.articles-panel tbody td {
  font-size: 13px;
  color: #333;
  padding: 12px 8px;
  border-bottom: 1px solid #eee;
  vertical-align: top;
}

.articles-panel tbody tr:hover {
  background: rgba(0, 0, 0, 0.03);
}

/* ✅ badge */
.articles-panel .badge {
  display: inline-flex;
  align-items: center;
  height: 22px;
  padding: 0 10px;
  border-radius: 999px;
  font-size: 12px;
  border: 1px solid #ddd;
  background: #fafafa;
  color: #333;
}

.articles-panel .badge.ok {
  border-color: #cfe9da;
  background: #eef8f2;
  color: #1f6b3a;
}

.articles-panel .badge.warn {
  border-color: #f3e2bf;
  background: #fff6e6;
  color: #8a5a00;
}

/* ✅ tag */
.articles-panel .tag {
  display: inline-flex;
  align-items: center;
  height: 22px;
  padding: 0 10px;
  border-radius: 999px;
  font-size: 12px;
  border: 1px solid #eee;
  background: #fafafa;
  color: #444;
}

/* ✅ 版面救火：避免搜尋/按鈕重疊 */
.articles-panel .field.search-field {
  flex: 1 1 320px;
  min-width: 240px;
}
.articles-panel .articles-actions {
  flex: 0 0 auto;
  margin-left: auto;
  display: flex;
  gap: 10px;
}

/* ✅ tiptap shell */
.tiptap-shell {
  border: 1px solid #ddd;
  background: #fafafa;
  border-radius: 10px;
  padding: 10px 12px;
  min-height: 260px;
}

.tiptap-shell :deep(.ProseMirror) {
  outline: none;
  min-height: 240px;
  font-size: 13px;
  line-height: 1.7;
  color: #333;
}

.tiptap-shell :deep(.ProseMirror p) {
  margin: 0 0 10px;
}

/* ✅ toolbar */
.tiptap-toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  align-items: center;

  border: 1px solid #ddd;
  background: #fff;
  border-radius: 10px;
  padding: 8px 10px;
  margin-bottom: 10px;
}

.tiptap-toolbar select {
  width: auto !important;
  flex: 0 0 auto;
  max-width: 260px;
}

.tb-btn {
  height: 32px;
  padding: 0 8px;
  border-radius: 8px;
  border: 1px solid #ddd;
  background: #fff;
  color: #333;
  cursor: pointer;
  font-size: 13px;
}

.tb-btn:hover {
  background: rgba(0, 0, 0, 0.03);
}

.tb-btn.active {
  border-color: #cfd8ff;
  background: #eef1ff;
}

.tb-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.tb-sep {
  width: 1px;
  height: 18px;
  background: #e6e6e6;
  margin: 0 4px;
}
/* ==========================================================
  ✅ Articles 版面救火：避免搜尋/按鈕重疊 + textarea 溢出
========================================================== */

/* ✅ row 讓每個欄位可以換行、且預設不互相擠爆 */
.articles-panel .row {
  display: flex;
  gap: 14px;
  flex-wrap: wrap;
  align-items: flex-end;
}

/* ✅ 每個 field 給個合理寬度策略 */
.articles-panel .field {
  flex: 0 0 auto;
  min-width: 160px;
}

/* ✅ 搜尋欄位：允許吃掉一整段寬度（重點） */
.articles-panel .field.search-field {
  flex: 1 1 320px; /* ✅ 有空間就長大，窄了就換行 */
  min-width: 240px;
}

/* ✅ 搜尋 input 要真的吃滿 field */
.articles-panel .field.search-field input {
  width: 100%;
  box-sizing: border-box;
}

/* ✅ 按鈕區：永遠在最右邊，不壓到 input */
.articles-panel .articles-actions {
  flex: 0 0 auto;
  margin-left: auto; /* ✅ 把按鈕推到右側 */
  display: flex;
  gap: 10px;
}

/* ✅ articles-card 內所有元素都用 border-box，避免溢出 */
.articles-card,
.articles-card * {
  box-sizing: border-box;
}

/* ✅ textarea 100% 寬 + 不溢出 */
.articles-card textarea {
  width: 100% !important;
  max-width: 100% !important;
  display: block;
}

/* ==========================================================
  ✅ 匯入 HTML 彈窗
========================================================== */
.html-import-mask {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
}

.html-import-modal {
  width: min(720px, 100%);
  max-height: calc(100vh - 60px);
  background: #fff;
  border-radius: 14px;
  padding: 18px 20px 16px;
  box-shadow: 0 22px 60px rgba(0, 0, 0, 0.28);
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.html-import-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 15px;
  color: #222;
}

.html-import-hint {
  margin: 0;
  font-size: 12px;
  color: #666;
  line-height: 1.6;
}

.html-import-textarea {
  width: 100%;
  min-height: 280px;
  font-family: "SF Mono", Menlo, Consolas, "Microsoft JhengHei", monospace;
  font-size: 12px;
  line-height: 1.5;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 10px;
  background: #fafafa;
  color: #333;
  outline: none;
  resize: vertical;
  box-sizing: border-box;
}

.html-import-textarea:focus {
  border-color: #888;
  background: #fff;
}

.html-import-mode {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #555;
  cursor: pointer;
}

.html-import-mode input[type="checkbox"] {
  width: auto;
  height: auto;
  margin: 0;
}

.html-import-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.html-import-actions .tb-btn.primary {
  border-color: #1f6b3a;
  background: #1f6b3a;
  color: #fff;
}

.html-import-actions .tb-btn.primary:hover {
  filter: brightness(1.06);
}

.html-import-actions .tb-btn.primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  filter: none;
}
</style>
